import 'react-toastify/dist/ReactToastify.css';
import {
  Button,
  Dialog,
  FormControlLabel,
  Radio,
  RadioGroup,
  RadioProps,
  styled,
} from '@mui/material';
import React from 'react';
import { toast } from 'react-toastify';

import { Icons } from '../../../../assets/SvgIcons';
import classes from './../TodoCard.module.css';
import { useAppDispatch, useAppSelector } from '@Store/hooks';
import {
  getPlayBookDetail,
  postSectionList,
} from '@Store/feature/playBookDetailSlice';
import { ComponentHelper } from '@Helper/Infra';

export enum DeleteType {
  Section = 'Section',
  Task = 'Task',
}

export enum DeletionStyle {
  none = 'none',
  onlyItem = 'onlyItem',
  includeChildren = 'includeChildren',
}

interface DeleteDialogProps {
  open: boolean;
  handleClose: (close: boolean) => void;
  deleteAction: () => void;
  deleteAndMoveTaskAction: () => void;
  type: DeleteType;
  subChildrenCount: number;
  callRefresh?: () => void;
  name: string;
  sectionId: string;
}

const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: '50%',
  width: 16,
  height: 16,
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 0 0 1px rgb(16 22 26 / 40%)'
      : 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
  backgroundColor: theme.palette.mode === 'dark' ? '#394b59' : '#f5f8fa',
  backgroundImage:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))'
      : 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: '1px auto  #C2C1D0',
    outlineOffset: 2,
  },

  'input:disabled ~ &': {
    boxShadow: 'none',
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(57,75,89,.5)'
        : 'rgba(206,217,224,.5)',
  },
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#C2C1D0',
  backgroundImage:
    'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&:before': {
    display: 'block',
    width: 16,
    height: 16,
    backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: '#C2C1D0',
  },
});

// Inspired by blueprintjs
function BpRadio(props: RadioProps) {
  return (
    <Radio
      sx={{
        '&:hover': {
          bgcolor: 'transparent',
        },
      }}
      disableRipple
      color="default"
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      {...props}
    />
  );
}

interface UndoProps {
  onUndo: () => void;
  message: string;
  taskName: string;
}

export const Undo = ({ onUndo, message, taskName }: UndoProps) => {
  const handleClick = () => {
    onUndo();
  };

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ padding: 10 }}>
        <Icons.CheckIcon />
      </div>
      <div style={{ padding: 10 }}>
        <div
          style={{ color: '#46426E', fontWeight: '700', fontFamily: 'Inter' }}
        >
          {message}
        </div>
        <div
          style={{ color: '#1C174D', fontWeight: '500', fontFamily: 'Inter' }}
        >
          {taskName}
        </div>
        <div>
          <div
            style={{
              color: '#5D54C0',
              fontFamily: 'Inter',
              marginTop: 10,
              cursor: 'pointer',
            }}
            onClick={handleClick}
          >
            UNDO ACTION
          </div>
        </div>
      </div>
    </div>
  );
};

export default function DeleteDialog(props: DeleteDialogProps) {
  const {
    open,
    handleClose,
    deleteAction,
    type,
    subChildrenCount,
    name,
    callRefresh,
    deleteAndMoveTaskAction,
    sectionId,
  } = props;
  const [deletionStyle, setDeletionStyle] = React.useState<DeletionStyle>(
    DeletionStyle.none
  );
  const dispatch = useAppDispatch();
  const { sectionList } = useAppSelector(getPlayBookDetail);
  const componentHelper = new ComponentHelper();
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeletionStyle((event.target as HTMLInputElement).value as DeletionStyle);
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="add-user-dialog"
      >
        <div className="dialog-header">
          <div className={classes.deleteTaskHeader}>
            Delete {type === DeleteType.Task ? 'task' : 'section'}
          </div>
          <button
            className="dialog-close-button"
            onClick={() => handleClose(false)}
          >
            <Icons.Cancel width="22" height="22" color="#000000" />
          </button>
        </div>

        <div className="dialog-content">
          <div className={classes.infoTextDelete}>
            {type === DeleteType.Task
              ? 'You are attempting to delete a task' +
                (subChildrenCount > 0
                  ? ' that have ' +
                    subChildrenCount.toString() +
                    ' subtask under it. '
                  : '. ') +
                'Be sure before delete it.'
              : 'You are attempting to delete a section ' +
                (subChildrenCount > 0
                  ? ' that have ' +
                    subChildrenCount.toString() +
                    ' of total children items under it. '
                  : '. ') +
                'Be sure before delete it.'}
          </div>
          <div style={{ marginTop: 20 }}>
            <RadioGroup
              aria-labelledby="demo-error-radios"
              className={classes.selection}
              name="quiz"
              value={deletionStyle}
              onChange={handleChange}
            >
              <FormControlLabel
                value={DeletionStyle.onlyItem}
                className={classes.deletionStyle}
                control={<BpRadio />}
                label={
                  type === DeleteType.Task
                    ? 'Delete only this task'
                    : 'Delete section and move items to untitled section'
                }
              />
              <FormControlLabel
                value={DeletionStyle.includeChildren}
                className={classes.deletionStyle}
                control={<BpRadio />}
                label={
                  type === DeleteType.Task
                    ? 'Delete this task and all its subtasks'
                    : 'Delete section and all items under it'
                }
              />
            </RadioGroup>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div></div>
            <div>
              <Button
                className={classes.CancelButton}
                onClick={() => handleClose(false)}
              >
                Cancel
              </Button>
              <Button
                className={classes.DeleteButton}
                onClick={() => {
                  if (deletionStyle === DeletionStyle.none) {
                    toast('Please select a type');
                    return;
                  } else {
                    dispatch(
                      postSectionList(
                        sectionList?.filter(
                          section => section.id !== sectionId
                        ) ?? []
                      )
                    );
                  }
                  if (deletionStyle === DeletionStyle.includeChildren) {
                    deleteAction();
                  } else {
                    deleteAndMoveTaskAction();
                  }
                  handleClose(true);
                  toast(
                    <Undo
                      onUndo={() => {
                        console.log('removed');
                        componentHelper
                          .undoDeleteSection(
                            sectionId,
                            JSON.parse(
                              localStorage.getItem(sectionId + 'delete') ?? ''
                            )
                          )
                          .then(() => {
                            if (callRefresh) callRefresh();
                          });
                      }}
                      message="Successfully deleted"
                      taskName={name}
                    />
                  );
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
